import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PageHero from "../components/PageHero";
import heroImage from "../assets/zeamays.png";
import { formatPrice } from "../data/products";
import { cancelOrder, loadUserOrders } from "../services/orders";
import { enrichOrderTracking } from "../shared/orderTracking";

const TRACKING_STEPS = [
  { key: "pending", label: "Pending" },
  { key: "processing", label: "Processing" },
  { key: "ready_for_delivery", label: "Out for delivery" },
  { key: "delivered", label: "Delivered" },
];

const STATUS_BADGE_STYLES = {
  pending: "border-amber-200 bg-amber-50 text-amber-800",
  processing: "border-blue-200 bg-blue-50 text-blue-800",
  ready_for_delivery: "border-violet-200 bg-violet-50 text-violet-800",
  delivered: "border-emerald-200 bg-emerald-50 text-emerald-800",
  cancelled: "border-red-200 bg-red-50 text-red-700",
};

const STATUS_PROGRESS_STYLES = {
  pending: "from-amber-400 to-amber-500",
  processing: "from-blue-400 to-blue-600",
  ready_for_delivery: "from-violet-400 to-violet-600",
  delivered: "from-emerald-400 to-emerald-600",
  cancelled: "from-red-400 to-red-600",
};

const formatOrderDate = (value) => {
  if (!value) {
    return "Unknown date";
  }

  return new Intl.DateTimeFormat("en-NG", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

const formatTrackingLabel = (value) =>
  value === "ready_for_delivery"
    ? "Out for delivery"
    : value.replaceAll("_", " ").replace(/\b\w/g, (match) => match.toUpperCase());

const getBadgeStyles = (status) => STATUS_BADGE_STYLES[status] || "border-slate-200 bg-slate-50 text-slate-700";

const getProgressStyles = (status) => STATUS_PROGRESS_STYLES[status] || STATUS_PROGRESS_STYLES.processing;

const Order = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [ordersReady, setOrdersReady] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [cancellationError, setCancellationError] = useState("");
  const [cancellingOrderId, setCancellingOrderId] = useState("");
  const [pendingCancellationOrder, setPendingCancellationOrder] = useState(null);
  const [successMessage] = useState(
    location.state?.paymentCompleted ? "Order completed successfully." : ""
  );

  useEffect(() => {
    let isActive = true;

    const fetchOrders = async ({ showLoading = true } = {}) => {
      if (showLoading) {
        setOrdersReady(false);
      }

      if (loading) {
        if (isActive) {
          setOrders([]);
          setErrorMessage("");
          setCancellationError("");
        }
        return;
      }

      if (!user?.id) {
        if (isActive) {
          setOrders([]);
          setErrorMessage("");
          setCancellationError("");
          setOrdersReady(true);
        }
        return;
      }

        try {
          const remoteOrders = await loadUserOrders(user.id);

        if (!isActive) {
          return;
        }

        // Always render the latest order records, never a checkout or local-cache snapshot.
        setOrders(remoteOrders);
        setErrorMessage("");
        setCancellationError("");
      } catch (error) {
        if (!isActive) {
          return;
        }

        setOrders([]);
        setErrorMessage(error.message || "We could not load your orders right now. Please try again.");
      } finally {
        if (isActive) {
          setOrdersReady(true);
        }
      }
    };

    fetchOrders();

    const refreshOrders = () => {
      if (user?.id && !loading) {
        fetchOrders({ showLoading: false });
      }
    };

    window.addEventListener("focus", refreshOrders);

    return () => {
      isActive = false;
      window.removeEventListener("focus", refreshOrders);
    };
  }, [loading, user?.id]);

  const trackedOrders = orders.map((order) => enrichOrderTracking(order)).filter(Boolean);
  const openOrders = trackedOrders.filter((order) => !["delivered", "cancelled"].includes(order.status));
  const closedOrders = trackedOrders.filter((order) => ["delivered", "cancelled"].includes(order.status));
  const isEligibleForCancellation = (trackingStatus) =>
    ["pending", "processing"].includes(trackingStatus);
  const orderCountLabel = trackedOrders.length.toLocaleString();
  const openCountLabel = openOrders.length.toLocaleString();
  const closedCountLabel = closedOrders.length.toLocaleString();

  const openCancellationModal = (order) => {
    setCancellationError("");
    setPendingCancellationOrder(order);
  };

  const closeCancellationModal = () => {
    if (cancellingOrderId) {
      return;
    }

    setPendingCancellationOrder(null);
  };

  const handleCancelOrder = async () => {
    if (!pendingCancellationOrder?.id) {
      return;
    }

    setCancellingOrderId(pendingCancellationOrder.id);
    setCancellationError("");

    try {
      const updatedOrder = await cancelOrder(pendingCancellationOrder.id);
      setOrders((currentOrders) =>
        currentOrders.map((order) => (order.id === updatedOrder.id ? updatedOrder : order))
      );
      setPendingCancellationOrder(null);
    } catch (error) {
      setCancellationError(error.message || "Unable to cancel this order.");
    } finally {
      setCancellingOrderId("");
    }
  };
  const renderOrderCard = (order) => {
              const trackingStatus = order.status;
              const isCancelled = trackingStatus === "cancelled";
              const canCancelOrder = isEligibleForCancellation(trackingStatus);
              const shipmentInfoAvailable = Boolean(
                order.delivery_provider ||
                  order.courier ||
                  order.shipment_id ||
                  order.tracking_number ||
                  order.tracking_url ||
                  order.delivery_status ||
                  order.delivery_eta ||
                  order.quoted_at
              );
              const trackingIndex = TRACKING_STEPS.findIndex((step) => step.key === trackingStatus);
              const progressPercentage = isCancelled ? 0 : trackingStatus === "delivered" ? 100 : Math.max(0, (trackingIndex + 1) * 25);

              return (
                <article
                  key={order.id}
                  className="overflow-hidden rounded-[2rem] border border-white/70 bg-white/90 shadow-[0_24px_80px_rgba(15,23,42,0.10)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_30px_90px_rgba(15,23,42,0.14)]"
                >
                  <div className="border-b border-slate-100 bg-[linear-gradient(135deg,rgba(255,251,235,0.9),rgba(255,255,255,1))] px-6 py-6 sm:px-8">
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <p className="text-sm uppercase tracking-[0.22em] text-slate-500">
                          {formatOrderDate(order.created_at)}
                        </p>
                        <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">
                          Order {order.payment_reference || order.id}
                        </h2>
                        <p className="mt-2 text-sm text-slate-600">
                          Your fulfilment status is updated by our team as your order progresses.
                        </p>
                      </div>

                      <div className="grid gap-3 sm:grid-cols-2 lg:min-w-[320px]">
                        <div className="rounded-[1.5rem] border border-slate-100 bg-white px-4 py-4 shadow-sm">
                          <p className="text-xs uppercase tracking-[0.22em] text-slate-500">
                            Tracking Status
                          </p>
                          <span
                            className={`mt-3 inline-flex rounded-full border px-3 py-1 text-sm font-semibold capitalize ${getBadgeStyles(
                              trackingStatus
                            )}`}
                          >
                            {formatTrackingLabel(trackingStatus)}
                          </span>
                        </div>

                        <div className="rounded-[1.5rem] border border-slate-100 bg-white px-4 py-4 shadow-sm">
                          <p className="text-xs uppercase tracking-[0.22em] text-slate-500">
                            Order Total
                          </p>
                          <p className="mt-3 text-2xl font-semibold text-slate-950">
                            {formatPrice(order.total_amount)}
                          </p>
                          {canCancelOrder ? (
                            <button
                              type="button"
                              onClick={() => openCancellationModal(order)}
                              className="mt-4 inline-flex w-full items-center justify-center rounded-full border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100"
                            >
                              Cancel Order
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-6 px-6 py-6 sm:px-8 lg:grid-cols-[1.15fr_0.85fr]">
                    <div className="space-y-6">
                      {isCancelled ? (
                        <div className="rounded-[1.5rem] border border-red-200 bg-red-50 px-4 py-4 text-red-700">
                          <p className="text-sm font-semibold uppercase tracking-[0.2em]">
                            Order Cancelled
                          </p>
                          <p className="mt-2 text-sm leading-7 text-red-700/90">
                            This order has been cancelled and is no longer being processed.
                          </p>
                        </div>
                      ) : null}

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="rounded-[1.5rem] bg-slate-50 px-4 py-4">
                          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">
                            Latest Status Update
                          </p>
                          <p className="mt-2 text-base font-semibold text-slate-950">
                            {formatOrderDate(order.status_updated_at || order.created_at)}
                          </p>
                        </div>
                        <div className="rounded-[1.5rem] bg-slate-50 px-4 py-4">
                          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">
                            Current Status
                          </p>
                          <p className="mt-2 text-base font-semibold text-slate-950">
                            {formatTrackingLabel(trackingStatus)}
                          </p>
                        </div>
                      </div>

                      {!isCancelled ? (
                        <div className="rounded-[1.5rem] border border-slate-100 bg-white px-4 py-4 shadow-sm">
                          <div className="mb-3 flex items-center justify-between text-sm">
                            <span className="font-medium text-slate-600">Delivery Progress</span>
                            <span className="font-semibold text-slate-900">{progressPercentage}%</span>
                          </div>
                          <div className="h-3 overflow-hidden rounded-full bg-slate-100">
                            <div
                              className={`h-full rounded-full bg-gradient-to-r ${getProgressStyles(
                                trackingStatus
                              )} transition-[width] duration-700 ease-out`}
                              style={{ width: `${progressPercentage}%` }}
                            />
                          </div>
                        </div>
                      ) : null}

                      {!isCancelled ? (
                        <div className="rounded-[1.5rem] border border-slate-100 bg-white px-4 py-5 shadow-sm">
                          <div className="mb-4 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-slate-950">Order Tracking Timeline</h3>
                            <p className="text-sm text-slate-500">
                              {trackingStatus === "delivered" ? "Completed" : "In progress"}
                            </p>
                          </div>

                          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                            {TRACKING_STEPS.map((step, index) => {
                              const isComplete = index <= trackingIndex;
                              const isCurrent = index === trackingIndex;
                              const isUpcoming = index === trackingIndex + 1;

                              return (
                                <div
                                  key={step.key}
                                  className={`rounded-[1.25rem] border px-4 py-4 transition duration-300 ${
                                    isComplete
                                      ? "border-emerald-200 bg-emerald-50/70"
                                      : isUpcoming
                                      ? "border-amber-200 bg-amber-50/70"
                                      : "border-slate-200 bg-slate-50/80"
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <span
                                      className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
                                        isComplete
                                          ? "bg-emerald-600 text-white"
                                          : isUpcoming
                                          ? "bg-amber-500 text-white"
                                          : "bg-white text-slate-400"
                                      }`}
                                      aria-hidden="true"
                                    >
                                      {isComplete ? "✓" : isUpcoming ? "⌛" : "⬜"}
                                    </span>
                                    <div>
                                      <p className="text-sm font-semibold text-slate-950">{step.label}</p>
                                      <p className="text-xs text-slate-500">
                                        {isComplete
                                          ? "Complete"
                                          : isCurrent
                                          ? "Current stage"
                                          : isUpcoming
                                          ? "Next step"
                                          : "Pending"}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}

                      <div className="rounded-[1.5rem] border border-slate-100 bg-white px-4 py-5 shadow-sm">
                        <h3 className="text-lg font-semibold text-slate-950">Status History</h3>
                        <div className="mt-3 space-y-2">
                          {(order.status_history || []).length ? (order.status_history || []).slice().reverse().map((entry) => (
                            <div key={entry.id || `${entry.status}-${entry.created_at}`} className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2 text-sm">
                              <span className="font-semibold text-slate-800">{formatTrackingLabel(entry.status)}</span>
                              <span className="text-slate-500">{formatOrderDate(entry.created_at)}</span>
                            </div>
                          )) : (
                            <p className="text-sm text-slate-500">{formatTrackingLabel(trackingStatus)} · {formatOrderDate(order.status_updated_at || order.created_at)}</p>
                          )}
                        </div>
                      </div>
                    </div>
                      {shipmentInfoAvailable ? (
                        <div className="rounded-[1.5rem] border border-slate-100 bg-white px-4 py-5 shadow-sm">
                          <div className="flex items-center justify-between gap-3">
                            <h3 className="text-lg font-semibold text-slate-950">Delivery & shipment</h3>
                            <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                              {formatTrackingLabel(order.delivery_status || trackingStatus || "pending")}
                            </span>
                          </div>

                          <div className="mt-3 space-y-2">
                            <div className="grid gap-1 sm:grid-cols-[160px_1fr]">
                              <dt className="text-sm font-semibold text-slate-500">Provider</dt>
                              <dd className="text-sm text-slate-700">
                                {order.delivery_provider || order.courier || "Not booked"}
                              </dd>
                            </div>
                            <div className="grid gap-1 sm:grid-cols-[160px_1fr]">
                              <dt className="text-sm font-semibold text-slate-500">Tracking number</dt>
                              <dd className="break-words text-sm text-slate-700">
                                {order.tracking_number || "Not available"}
                              </dd>
                            </div>
                            <div className="grid gap-1 sm:grid-cols-[160px_1fr]">
                              <dt className="text-sm font-semibold text-slate-500">Shipment status</dt>
                              <dd className="text-sm text-slate-700">
                                {formatTrackingLabel(order.delivery_status || trackingStatus || "pending")}
                              </dd>
                            </div>
                            <div className="grid gap-1 sm:grid-cols-[160px_1fr]">
                              <dt className="text-sm font-semibold text-slate-500">Estimated delivery</dt>
                              <dd className="text-sm text-slate-700">
                                {formatOrderDate(order.delivery_eta || order.estimated_delivery_date)}
                              </dd>
                            </div>
                          </div>

                          <div className="mt-4 flex flex-wrap gap-3">
                            {order.tracking_url ? (
                              <a
                                href={order.tracking_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center justify-center rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                              >
                                Track Shipment
                              </a>
                            ) : null}
                            {order.shipment_id ? (
                              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700">
                                Shipment ID: {order.shipment_id}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      ) : null}

                    <div className="rounded-[1.5rem] border border-slate-100 bg-slate-50/80 px-4 py-4 shadow-sm">
                      <h3 className="text-lg font-semibold text-slate-950">Items in this order</h3>
                      <div className="mt-4 space-y-3">
                        {(order.order_items || []).map((item) => (
                          <div
                            key={`${order.id}-${item.id}-${item.product_name}`}
                            className="rounded-[1.25rem] bg-white px-4 py-4 shadow-sm"
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <p className="font-semibold text-slate-950">{item.product_name}</p>
                                <p className="mt-1 text-sm text-slate-500">Quantity x {item.quantity}</p>
                              </div>
                              <p className="text-sm font-semibold text-slate-950">
                                {formatPrice(item.price * item.quantity)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
      );
  };

  return (
    <div className="min-h-screen bg-[#f7fbef]">
      <PageHero
        eyebrow="Order tracking"
        title="Track every delivery in one place."
        description="Follow your orders from checkout to delivery with a clear, customer-first view of every status update."
        image={heroImage}
        imageAlt="Zea Mays order tracking"
        primaryAction={{ to: "/products", label: "Shop More" }}
        secondaryAction={{ to: "/profile", label: "My Profile" }}
        stats={[
          { label: "Orders", value: orderCountLabel },
          { label: "Open", value: openCountLabel },
          { label: "Closed", value: closedCountLabel },
          { label: "Status", value: "Live updates" },
        ]}
        compact
      />

      <div className="px-4 py-12 sm:px-6">
        <div className="mx-auto max-w-6xl">

        {errorMessage ? (
          <p className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {errorMessage}
          </p>
        ) : null}

        {successMessage ? (
          <p className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {successMessage}
          </p>
        ) : null}

        {cancellationError ? (
          <p className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {cancellationError}
          </p>
        ) : null}

        {!ordersReady || loading ? (
          <div className="mx-auto max-w-xl rounded-[2rem] border border-white/70 bg-white/90 p-10 text-center shadow-[0_24px_80px_rgba(15,23,42,0.10)] backdrop-blur">
            <p className="text-sm uppercase tracking-[0.28em] text-amber-700">Loading orders</p>
            <h2 className="mt-6 text-2xl font-semibold text-slate-950">Fetching your order history</h2>
            <p className="mt-3 text-sm leading-7 text-slate-600">
              We are loading the current account&apos;s orders. This prevents another account&apos;s
              history from flashing on screen.
            </p>
          </div>
        ) : trackedOrders.length === 0 ? (
          <div className="mx-auto max-w-xl rounded-[2rem] border border-white/70 bg-white/90 p-10 text-center shadow-[0_24px_80px_rgba(15,23,42,0.10)] backdrop-blur">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-700">
              <span className="text-xl font-semibold">0</span>
            </div>
            <h2 className="mt-6 text-2xl font-semibold text-slate-950">You have no orders yet</h2>
            <p className="mt-3 text-sm leading-7 text-slate-600">
              When you complete checkout, your order tracking cards will appear here with payment,
              delivery, and timeline details.
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex rounded-full bg-slate-950 px-6 py-3 font-semibold text-white transition hover:bg-slate-800"
            >
              Continue Shopping
            </Link>
          </div>
        ) : (
          <div className="space-y-10">
            <section className="space-y-6">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm uppercase tracking-[0.28em] text-amber-700">Open Orders</p>
                  <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">
                    Active deliveries
                  </h2>
                </div>
                <span className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700">
                  {openOrders.length}
                </span>
              </div>

              {openOrders.length > 0 ? (
                <div className="space-y-6">{openOrders.map(renderOrderCard)}</div>
              ) : (
                <div className="rounded-[2rem] border border-white/70 bg-white/90 p-8 text-center shadow-[0_24px_80px_rgba(15,23,42,0.10)] backdrop-blur">
                  <p className="text-sm text-slate-600">No open orders right now.</p>
                </div>
              )}
            </section>

            <section className="space-y-6">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm uppercase tracking-[0.28em] text-slate-500">Closed Orders</p>
                  <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">
                    Cancelled or completed
                  </h2>
                </div>
                <span className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700">
                  {closedOrders.length}
                </span>
              </div>

              {closedOrders.length > 0 ? (
                <div className="space-y-6">{closedOrders.map(renderOrderCard)}</div>
              ) : (
                <div className="rounded-[2rem] border border-white/70 bg-white/90 p-8 text-center shadow-[0_24px_80px_rgba(15,23,42,0.10)] backdrop-blur">
                  <p className="text-sm text-slate-600">No closed orders yet.</p>
                </div>
              )}
            </section>
          </div>
        )}
        {pendingCancellationOrder ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 py-6 backdrop-blur-sm">
            <div className="w-full max-w-md rounded-[2rem] border border-white/70 bg-white p-6 shadow-[0_30px_100px_rgba(15,23,42,0.25)]">
              <p className="text-sm uppercase tracking-[0.24em] text-red-600">Confirm cancellation</p>
              <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                Are you sure you want to cancel this order?
              </h2>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={closeCancellationModal}
                  disabled={Boolean(cancellingOrderId)}
                  className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Keep Order
                </button>
                <button
                  type="button"
                  onClick={handleCancelOrder}
                  disabled={Boolean(cancellingOrderId)}
                  className="inline-flex items-center justify-center rounded-full bg-red-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {cancellingOrderId === pendingCancellationOrder.id ? "Cancelling..." : "Cancel Order"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      </div>
    </div>
  );
};

export default Order;
